/**
 * Iter 97 — step-by-step mobile Pickup workflow (spec §7):
 * Customer → Vehicle → Inspection → Agreement → Payment → Complete Pickup.
 * Persists via the existing /bookings/{id}/checklist APIs (phase=pickup).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Money from "@/components/Money";
import DamageDiagramCanvas from "@/components/inspection/DamageDiagramCanvas";
import SignaturePad from "@/components/SignaturePad";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Phone, MessageCircle, CheckCircle2, ExternalLink } from "lucide-react";
import { WizardShell, WizardNav, FuelPicker, Row } from "@/components/ops/wizardShared";

const STEPS = ["Customer", "Vehicle", "Inspection", "Agreement", "Payment"];
const PAY_METHODS = ["Cash", "Card at pickup", "Bank transfer", "Already paid online"];

export default function PickupWizard({ booking, onClose, onDone }) {
  const open = !!booking;
  const [step, setStep] = useState(0);
  const [checklist, setChecklist] = useState(null);
  const [fuel, setFuel] = useState("");
  const [mileage, setMileage] = useState("");
  const [notes, setNotes] = useState("");
  const [markers, setMarkers] = useState([]);
  const [signature, setSignature] = useState("");
  const [payMethod, setPayMethod] = useState("");
  const [collected, setCollected] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(0); setChecklist(null); setSignature(""); setPayMethod(""); setBusy(false);
    setCollected(booking.amount_due_at_pickup ? String(booking.amount_due_at_pickup) : "");
    api.get(`/bookings/${booking.id}/checklist`).then((r) => {
      const pk = r.data.pickup || {};
      setChecklist(r.data);
      setFuel(pk.fuel_level || "");
      setMileage(pk.mileage_reading ?? "");
      setNotes(pk.notes || "");
      setMarkers(pk.damage_markers || []);
    }).catch((e) => toast.error(formatApiError(e)));
  }, [open, booking]);

  if (!open) return null;

  const saveChecklist = async (fields) => {
    try {
      const { data } = await api.patch(`/bookings/${booking.id}/checklist`, { phase: "pickup", ...fields });
      setChecklist(data);
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    }
  };

  const next = async () => {
    setBusy(true);
    try {
      if (step === 1) {
        if (!(await saveChecklist({ fuel_level: fuel, mileage_reading: mileage === "" ? undefined : mileage }))) return;
      } else if (step === 2) {
        if (!(await saveChecklist({ damage_markers: markers, notes }))) return;
      } else if (step === 3 && signature) {
        try {
          const { data } = await api.post(`/bookings/${booking.id}/checklist/sign`, { phase: "pickup", party: "vendor", image: signature });
          setChecklist(data);
          setSignature("");
          toast.success("Signed as provider");
        } catch (e) { toast.error(formatApiError(e)); return; }
      } else if (step === 4) {
        await api.post(`/bookings/${booking.id}/pickup/complete`, {
          payment_method: payMethod || null,
          collected_amount: collected === "" ? null : Number(collected),
        });
        toast.success("Pickup completed — rental is now active 🚗");
        onDone?.();
        return;
      }
      setStep((s) => Math.min(s + 1, STEPS.length - 1));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const cust = booking.customer || {};
  const wa = String(cust.whatsapp || cust.phone || "").replace(/[^\d+]/g, "");
  const pk = checklist?.pickup || {};
  const due = Number(booking.amount_due_at_pickup || 0);

  return (
    <WizardShell open={open} onClose={onClose} title={`Pickup — ${booking.car?.make} ${booking.car?.model}`} step={step} steps={STEPS} testId="pickup-wizard">
      {step === 0 && (
        <div data-testid="pickup-step-customer">
          <Row label="Customer" value={cust.name || "—"} strong />
          <Row label="Phone" value={cust.phone || "—"} />
          <Row label="Email" value={cust.email || "—"} />
          <Row label="Reservation" value={`#${(booking.id || "").slice(0, 8).toUpperCase()}`} />
          <Row label="Dates" value={`${String(booking.start_date || "").slice(0, 10)} → ${String(booking.end_date || "").slice(0, 10)}`} />
          {wa && (
            <div className="grid grid-cols-2 gap-2 mt-4">
              <a href={`tel:${wa}`} className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-border py-3 text-sm font-bold"><Phone className="h-4 w-4" /> Call</a>
              <a href={`https://wa.me/${wa.replace("+", "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 py-3 text-sm font-bold"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
            </div>
          )}
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4" data-testid="pickup-step-vehicle">
          <Row label="Vehicle" value={`${booking.car?.make} ${booking.car?.model}`} strong />
          <Row label="Plate" value={booking.car?.license_plate || "—"} />
          <div>
            <div className="text-sm font-bold mb-2">Fuel level at pickup</div>
            <FuelPicker value={fuel} onChange={setFuel} testIdPrefix="pickup-fuel" />
          </div>
          <div>
            <div className="text-sm font-bold mb-2">Mileage (km)</div>
            <Input type="number" inputMode="numeric" value={mileage} onChange={(e) => setMileage(e.target.value)} placeholder="e.g. 45200" className="rounded-xl h-12 text-lg" data-testid="pickup-mileage" />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4" data-testid="pickup-step-inspection">
          <div className="text-sm text-neutral-500">Tap the diagram to mark existing damage. Add photos and notes below.</div>
          <DamageDiagramCanvas
            bodyType={pk.damage_body_type || checklist?.body_type}
            markers={markers}
            onChange={setMarkers}
            canEdit
            testId="pickup-wizard-diagram"
          />
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Inspection notes (optional)" className="rounded-xl" data-testid="pickup-notes" />
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4" data-testid="pickup-step-agreement">
          <div className="space-y-1">
            <Row label="Customer signature" value={pk.customer_signature ? <span className="inline-flex items-center gap-1 text-emerald-600 font-bold"><CheckCircle2 className="h-4 w-4" /> Signed</span> : "Not signed"} />
            <Row label="Provider signature" value={pk.vendor_signature ? <span className="inline-flex items-center gap-1 text-emerald-600 font-bold"><CheckCircle2 className="h-4 w-4" /> Signed</span> : "Not signed"} />
          </div>
          {!pk.vendor_signature && (
            <div>
              <div className="text-sm font-bold mb-2">Sign as provider</div>
              <SignaturePad value={signature} onChange={setSignature} onClear={() => setSignature("")} />
            </div>
          )}
          <Link to={`/agreements/${booking.id}`} className="inline-flex items-center gap-1.5 text-sm font-bold text-blue-600" data-testid="pickup-open-agreement">
            Open full rental agreement <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4" data-testid="pickup-step-payment">
          <div>
            <Row label="Rental total" value={<Money amount={booking.amount || 0} />} strong />
            <Row label="Paid online" value={<Money amount={booking.amount_paid_online || 0} />} />
            <Row label="Remaining at pickup" value={<Money amount={due} />} strong />
            <Row label="Deposit" value={<Money amount={booking.deposit_amount || 0} />} />
          </div>
          {due > 0 && (
            <>
              <div>
                <div className="text-sm font-bold mb-2">Payment method</div>
                <div className="grid grid-cols-2 gap-2">
                  {PAY_METHODS.map((m) => (
                    <button key={m} type="button" onClick={() => setPayMethod(m)} className={`rounded-xl border py-3 text-xs font-bold ${payMethod === m ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-border text-neutral-600"}`} data-testid={`pay-method-${m.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                      {m}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-sm font-bold mb-2">Amount collected (€)</div>
                <Input type="number" inputMode="decimal" value={collected} onChange={(e) => setCollected(e.target.value)} className="rounded-xl h-12 text-lg" data-testid="pickup-collected" />
              </div>
            </>
          )}
        </div>
      )}

      <WizardNav
        step={step}
        lastStep={STEPS.length - 1}
        onBack={() => setStep((s) => Math.max(0, s - 1))}
        onNext={next}
        busy={busy}
        nextLabel={step === STEPS.length - 1 ? "Complete Pickup" : "Next"}
        nextTestId={step === STEPS.length - 1 ? "complete-pickup-btn" : "wizard-next"}
      />
    </WizardShell>
  );
}
